import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type { WatchFolder } from './vod-watch-folders-types';

// ============================================
// Query keys
// ============================================

export const vodWatchFolderKeys = {
  all: ['vod', 'watch-folders'] as const,
  list: (serviceId: string) =>
    ['vod', 'watch-folders', 'list', serviceId] as const,
  detail: (serviceId: string, watchFolderId: string | number) =>
    ['vod', 'watch-folders', 'detail', serviceId, watchFolderId] as const,
  status: (serviceId: string, watchFolderId: string | number) =>
    ['vod', 'watch-folders', 'status', serviceId, watchFolderId] as const,
};

// ============================================
// Queries
// ============================================

export function useWatchFolders(serviceId: string) {
  return useQuery<WatchFolder[]>({
    queryKey: vodWatchFolderKeys.list(serviceId),
    enabled: !!serviceId,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'vod')}/watch-folders`);
      return res.data?.data ?? res.data ?? [];
    },
  });
}

export function useWatchFolderDetail(
  serviceId: string,
  watchFolderId: string | number | null,
) {
  return useQuery<WatchFolder>({
    queryKey: vodWatchFolderKeys.detail(serviceId, watchFolderId ?? ''),
    enabled: !!serviceId && !!watchFolderId,
    queryFn: async () => {
      const res = await serviceApiV3.get(
        `${mediaBase(serviceId, 'vod')}/watch-folders/${watchFolderId}`,
      );
      return res.data?.data ?? res.data;
    },
  });
}

export function useWatchFolderStatus(
  serviceId: string,
  watchFolderId: string | number | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: vodWatchFolderKeys.status(serviceId, watchFolderId ?? ''),
    enabled: !!serviceId && !!watchFolderId && (options?.enabled ?? true),
    queryFn: async () => {
      const res = await serviceApiV3.get(
        `${mediaBase(serviceId, 'vod')}/watch-folders/${watchFolderId}/status`,
      );
      return res.data?.data ?? res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useCreateWatchFolder(serviceId: string) {
  const qc = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation<any, Error, any>({
    mutationFn: async (payload) => {
      const res = await serviceApiV3.post(`${mediaBase(serviceId, 'vod')}/watch-folders`, payload);
      return res.data?.data ?? res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vodWatchFolderKeys.all });
    },
  });
}

export function useDeleteWatchFolder(
  serviceId: string,
  options?: UseMutationOptions<void, Error, { watchFolderId: string | number }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ watchFolderId }: { watchFolderId: string | number }) => {
      await serviceApiV3.delete(
        `${mediaBase(serviceId, 'vod')}/watch-folders/${watchFolderId}`,
      );
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vodWatchFolderKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

/** Enable a paused watch folder. */
export function useEnableWatchFolder(
  serviceId: string,
  options?: UseMutationOptions<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    Error,
    { watchFolderId: string | number }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ watchFolderId }: { watchFolderId: string | number }) => {
      // Legacy sends a literal empty object body — keep it for server compatibility.
      const res = await serviceApiV3.put(
        `${mediaBase(serviceId, 'vod')}/watch-folders/${watchFolderId}/enable`,
        {},
      );
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vodWatchFolderKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}
